import React, { useState } from 'react';
import { importExportService } from '../services/api/importExportService';

const ImportExportPage: React.FC = () => {
  const [tipoSelecionado, setTipoSelecionado] = useState<'pacientes' | 'consultas' | 'catalogos'>('pacientes');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [status, setStatus] = useState<{ tipo: 'success' | 'error' | 'info', mensagem: string, detalhes?: string[] } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDownloadExemplo = async () => {
    try {
      setIsLoading(true);
      setStatus({ tipo: 'info', mensagem: 'Gerando arquivo de exemplo...' });
      
      console.log(`[ImportExportPage] Solicitando exemplo para ${tipoSelecionado}...`);
      
      const blob = await importExportService.baixarExemplo(tipoSelecionado);
      
      // Baixar arquivo
      importExportService.baixarArquivo(blob, `exemplo-${tipoSelecionado}.csv`);
      
      setStatus({ tipo: 'success', mensagem: `Arquivo de exemplo para ${tipoSelecionado} baixado com sucesso!` });
    } catch (error: any) {
      console.error('[ImportExportPage] Erro ao baixar exemplo:', error);
      
      const errorMessage = error.response?.data?.mensagem || 
                          error.message || 
                          'Erro desconhecido';
      
      setStatus({ 
        tipo: 'error', 
        mensagem: `Falha ao baixar exemplo: ${errorMessage}` 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportar = async () => {
    try {
      setIsLoading(true);
      setStatus({ tipo: 'info', mensagem: 'Iniciando exportação...' });
      
      console.log(`[ImportExportPage] Exportando dados de ${tipoSelecionado}...`);
      
      const blob = await importExportService.exportarDados(tipoSelecionado);
      
      // Baixar arquivo com data atual
      const dataAtual = new Date().toISOString().split('T')[0];
      importExportService.baixarArquivo(blob, `${tipoSelecionado}-${dataAtual}.csv`);
      
      setStatus({ tipo: 'success', mensagem: `Exportação de ${tipoSelecionado} realizada com sucesso!` });
    } catch (error: any) {
      console.error('[ImportExportPage] Erro na exportação:', error);
      
      const errorMessage = error.response?.data?.mensagem || 
                          error.message || 
                          'Erro desconhecido';
      
      setStatus({ 
        tipo: 'error', 
        mensagem: `Falha na exportação: ${errorMessage}` 
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleImportar = async () => {
    if (!arquivo) {
      setStatus({ tipo: 'error', mensagem: 'Selecione um arquivo CSV para importar.' });
      return;
    }
    
    try {
      setIsLoading(true);
      setStatus({ tipo: 'info', mensagem: 'Lendo arquivo e validando dados...' });
      
      console.log(`[ImportExportPage] Importando arquivo ${arquivo.name} para ${tipoSelecionado}...`);
      
      // Ler conteúdo do arquivo
      const csvContent = await arquivo.text();
      
      // Enviar para o serviço
      const resultado = await importExportService.importarDados(tipoSelecionado, csvContent);
      
      if (!resultado.sucesso) {
        setStatus({ 
          tipo: 'error', 
          mensagem: resultado.mensagem || 'Erro desconhecido na importação',
          detalhes: resultado.detalhes
        });
        return;
      }
      
      setStatus({ 
        tipo: 'success', 
        mensagem: resultado.mensagem || `Importação de ${tipoSelecionado} realizada com sucesso!`
      });
      
      // Limpar arquivo após sucesso
      setArquivo(null);
      
      // Reset file input
      const fileInput = document.getElementById('arquivoCSV') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error: any) {
      console.error('[ImportExportPage] Erro na importação:', error);
      
      const errorMessage = error.response?.data?.mensagem || 
                          error.message || 
                          'Erro desconhecido';
      
      setStatus({ 
        tipo: 'error', 
        mensagem: `Falha na importação: ${errorMessage}`,
        detalhes: error.response?.data?.detalhes
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setStatus({ tipo: 'error', mensagem: 'Por favor, selecione um arquivo CSV válido.' });
        return;
      }
      
      setArquivo(file);
      setStatus(null);
    }
  };

  const renderDetalhesFormato = () => {
    switch(tipoSelecionado) {
      case 'pacientes':
        return (
          <div>
            <h6>Campos para Pacientes:</h6>
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-success">Obrigatórios:</h6>
                <ul>
                  <li><strong>nome</strong>: Nome completo do paciente</li>
                  <li><strong>servico</strong>: psicoterapia, avaliacao, orientacao, terapia_casal, triagem</li>
                </ul>
              </div>
              <div className="col-md-6">
                <h6 className="text-info">Opcionais:</h6>
                <ul>
                  <li><strong>cpf</strong>: CPF (apenas números)</li>
                  <li><strong>email</strong>: Email válido</li>
                  <li><strong>telefone</strong>: Telefone de contato</li>
                  <li><strong>data_nascimento</strong>: YYYY-MM-DD</li>
                  <li><strong>genero</strong>: M, F, Outro, Prefiro não informar</li>
                  <li><strong>endereco, cep, cidade, estado</strong></li>
                  <li><strong>nome_emergencia, telefone_emergencia</strong></li>
                  <li><strong>usuario_responsavel</strong>: ID do usuário responsável (deve existir na empresa)</li>
                  <li>E muitos outros campos...</li>
                </ul>
                <div className="alert alert-info mt-2 mb-0">
                  <small><i className="fas fa-info-circle"></i> <strong>Nota:</strong> Se usuario_responsavel não for especificado ou não existir na empresa, o usuário logado será definido como responsável.</small>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'consultas':
        return (
          <div>
            <h6>Campos para Consultas:</h6>
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-success">Obrigatórios:</h6>
                <ul>
                  <li><strong>paciente_id</strong>: ID do paciente (número)</li>
                  <li><strong>data_consulta</strong>: YYYY-MM-DD</li>
                  <li><strong>hora_inicio</strong>: HH:MM</li>
                  <li><strong>valor</strong>: Valor da consulta (decimal)</li>
                </ul>
              </div>
              <div className="col-md-6">
                <h6 className="text-info">Opcionais:</h6>
                <ul>
                  <li><strong>usuario_id</strong>: ID do profissional (deve existir na empresa)</li>
                  <li><strong>hora_fim</strong>: HH:MM</li>
                  <li><strong>status_consulta</strong>: agendada, realizada, cancelada, faltou</li>
                  <li><strong>status_pagamento</strong>: pendente, pago, cancelado</li>
                  <li><strong>forma_pagamento</strong>: dinheiro, pix, cartao, transferencia</li>
                  <li><strong>observacoes_agendamento</strong>: Texto livre</li>
                </ul>
                <div className="alert alert-info mt-2 mb-0">
                  <small><i className="fas fa-info-circle"></i> <strong>Nota:</strong> Se usuario_id não for especificado ou não existir na empresa, o usuário logado será definido como responsável pela consulta.</small>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'catalogos':
        return (
          <div>
            <h6>Campos para Catálogos:</h6>
            <ul>
              <li><strong>tipo</strong>: "queixa" ou "sintoma"</li>
              <li><strong>valor</strong>: Texto da queixa ou sintoma</li>
            </ul>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-exchange-alt mr-2"></i>
                Importação e Exportação de Dados
              </h3>
            </div>
            
            <div className="card-body">
              {status && (
                <div className={`alert alert-${status.tipo === 'error' ? 'danger' : status.tipo === 'success' ? 'success' : 'info'} alert-dismissible`}>
                  <button type="button" className="close" onClick={() => setStatus(null)}>
                    <span>&times;</span>
                  </button>
                  <div>{status.mensagem}</div>
                  {status.detalhes && (
                    <div className="mt-2">
                      <strong>Detalhes dos erros:</strong>
                      <ul className="mb-0 mt-1">
                        {status.detalhes.map((detalhe, index) => (
                          <li key={index}>{detalhe}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="tipoEntidade">
                  <i className="fas fa-list mr-2"></i>
                  Selecione o tipo de dados:
                </label>
                <select 
                  id="tipoEntidade" 
                  className="form-control" 
                  value={tipoSelecionado}
                  onChange={(e) => setTipoSelecionado(e.target.value as any)}
                  disabled={isLoading}
                >
                  <option value="pacientes">📋 Pacientes</option>
                  <option value="consultas">📅 Consultas</option>
                  <option value="catalogos">📚 Catálogos (Queixas e Sintomas)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h3 className="card-title">
                <i className="fas fa-download mr-2"></i>
                Exportar Dados
              </h3>
            </div>
            
            <div className="card-body">
              <p>Baixe os dados atuais em formato CSV para backup ou manipulação externa.</p>
              
              <div className="btn-group d-block" role="group">
                <button 
                  type="button" 
                  className="btn btn-outline-info btn-block mb-2" 
                  onClick={handleDownloadExemplo}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Gerando exemplo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-download mr-2"></i> 
                      Baixar Arquivo de Exemplo
                    </>
                  )}
                </button>
                
                <button 
                  type="button" 
                  className="btn btn-success btn-block" 
                  onClick={handleExportar}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      Exportando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download mr-2"></i> 
                      Exportar {tipoSelecionado}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="card-title">
                <i className="fas fa-upload mr-2"></i>
                Importar Dados
              </h3>
            </div>
            
            <div className="card-body">
              <p>Importe dados em formato CSV. Os dados serão adicionados aos existentes (sem duplicatas).</p>
              
              <div className="form-group">
                <label htmlFor="arquivoCSV">
                  <i className="fas fa-file-csv mr-2"></i>
                  Selecione um arquivo CSV:
                </label>
                <input 
                  type="file" 
                  className="form-control-file" 
                  id="arquivoCSV"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
                {arquivo && (
                  <small className="text-success">
                    <i className="fas fa-check mr-1"></i>
                    Arquivo selecionado: {arquivo.name} ({(arquivo.size / 1024).toFixed(2)} KB)
                  </small>
                )}
              </div>
              
              <button 
                type="button" 
                className="btn btn-primary btn-block"
                onClick={handleImportar}
                disabled={isLoading || !arquivo}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Importando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload mr-2"></i>
                    Importar {arquivo ? arquivo.name : 'Arquivo'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-info-circle mr-2"></i>
                Estrutura dos Dados - {tipoSelecionado}
              </h3>
            </div>
            
            <div className="card-body">
              {renderDetalhesFormato()}
              
              <div className="alert alert-warning mt-3">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Importante:</strong>
                <ul className="mb-0 mt-2">
                  <li>Baixe o arquivo de exemplo para ver a estrutura correta</li>
                  <li>Campos de data devem estar no formato YYYY-MM-DD</li>
                  <li>Campos de hora devem estar no formato HH:MM</li>
                  <li>Valores monetários devem usar ponto como separador decimal</li>
                  <li>O sistema não importará registros duplicados (baseado em CPF para pacientes)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExportPage;
